import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { CoachMatchingDebug } from "~/components/coach-matching-debug";
import { CoachNotFound } from "~/components/coach-not-found";
import { getTestimonialAggregate } from "~/components/coach-profile/featured-testimonial";
import { CoachHero } from "~/components/coach-profile/hero";
import { CoachProfileSections } from "~/components/coach-profile/profile-sections";
import { CoachTabNav } from "~/components/coach-profile/tab-nav";
import { CoachTrustBand } from "~/components/coach-profile/trust-band";
import { PreGeneratedProfileBanner } from "~/components/ui/pre-generated-profile-banner";
import { coachCihBackRefsQueryOptions } from "~/lib/functions/cih/coaches";
import { trackCoachProfileViewed } from "~/lib/monitoring/track-event";
import { coachQueryOptions } from "~/lib/query-options";
import {
  createBreadcrumbStructuredData,
  createMetaTags,
  createPersonStructuredData,
  createProfilePageStructuredData,
  createServiceStructuredData,
} from "~/lib/shared/meta";
import { getCoachMatchingDebugData } from "~/routes/$username/_profile";

const profileSearchSchema = z.object({
  debug: z.literal("matching").optional(),
});

export const Route = createFileRoute("/$username/_profile/")({
  validateSearch: (search) => profileSearchSchema.parse(search),
  loader: async ({ context, params, location }) => {
    // Pre-load coach data into React Query using centralized query options
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    // CIH back-refs (frameworks/challenges/articles) decide which sections
    // render and how they are numbered, so they must settle before the server
    // renders: the client's first render sees only what the loader dehydrated,
    // and a query that finishes mid-render makes the two disagree (React #418).
    // prefetchQuery never throws, so a failed lookup hides those sections
    // instead of failing the whole profile. The other offers follow the same
    // rule: fetched after hydration, they would appear late and push the
    // footer down. prefetchQuery ignores `enabled`, so honour it here.
    // The "Take my assessment" block decides whether "Also from me" renders
    // and how sections are numbered, so it settles here too. A failed lookup
    // hides the block rather than failing the profile.
    let hasWheelPage = false;
    if (coach?.id) {
      const otherOffers = coachQueryOptions.otherOffers(
        coach.id,
        coach.membershipPlan ?? undefined,
      );
      const { getCoachWheelProfileBlockFn } =
        await import("~/lib/functions/wheel");
      [, , hasWheelPage] = await Promise.all([
        context.queryClient.prefetchQuery(
          coachCihBackRefsQueryOptions(coach.id),
        ),
        otherOffers.enabled
          ? context.queryClient.prefetchQuery(otherOffers)
          : undefined,
        getCoachWheelProfileBlockFn({
          data: { username: coach.username },
        }).catch(() => false),
      ]);
    }

    // Load debug data if in development mode and debug=matching is present
    const searchParams = profileSearchSchema.safeParse(location.search);
    const isDebugMode =
      process.env.NODE_ENV === "development" &&
      searchParams.success &&
      searchParams.data.debug === "matching";

    const debugData = isDebugMode
      ? await getCoachMatchingDebugData({ data: { username: params.username } })
      : null;

    return { coach, debugData, hasWheelPage };
  },
  head: ({ loaderData }) => {
    const coach = loaderData?.coach;
    if (!coach || !coach.profile) {
      return createMetaTags({
        title: "Coach Profile | Dream Coach Match",
        description: "This coach profile could not be found.",
        noindexFollow: true,
      });
    }

    const description =
      coach.profile.description ||
      `${coach.fullName} is a professional coach specializing in ${coach.profile.coachingTitle || "life coaching"}.`;

    // Determine best image for sharing
    const imageUrl =
      coach.profile.mainImage ||
      coach.profilePictureUrl ||
      `https://dreamcoachmatch.com/${coach.username}/social-image.svg`;

    const currentUrl = `https://dreamcoachmatch.com/${coach.username}`;

    // Generate keywords from coach data
    const keywords = [
      "coaching",
      "life coach",
      coach.profile.coachingTitle || undefined,
      coach.profile.primaryCity || undefined,
      coach.profile.languages || undefined,
      ...coach.certifications.map((cert) => cert.title),
    ]
      .filter(Boolean)
      .filter((keyword): keyword is string => typeof keyword === "string");

    // The coaching title makes each profile's <title> unique and descriptive
    // instead of "{name} | Dream Coach Match" across all 200 profiles.
    const pageTitle = coach.profile.coachingTitle
      ? `${coach.fullName}: ${coach.profile.coachingTitle} | Dream Coach Match`
      : `${coach.fullName} | Dream Coach Match`;

    const metaTags = createMetaTags({
      title: pageTitle,
      description,
      ogDescription: description,
      ogImage: imageUrl,
      ogUrl: currentUrl,
      ogType: "profile",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      canonical: currentUrl,
      author: coach.fullName,
      keywords,
    });

    const socialLinks = [
      coach.profile.website,
      coach.profile.linkedinProfile,
      coach.profile.facebookProfile,
      coach.profile.instagramProfile,
      coach.profile.youtubeChannel,
    ]
      .map((value) =>
        typeof value === "string" && value.trim().length > 0
          ? value.trim()
          : undefined,
      )
      .filter((value): value is string => Boolean(value));

    // Enhanced structured data using helper
    const personStructuredData = createPersonStructuredData({
      name: coach.fullName,
      jobTitle: coach.profile.title || coach.profile.coachingTitle || undefined,
      image: coach.profile.mainImage || coach.profilePictureUrl || undefined,
      url: currentUrl,
      description,
      sameAs: socialLinks,
      ...(coach.profile.primaryCity && {
        address: {
          addressLocality: coach.profile.primaryCity,
        },
      }),
    });

    const profilePageStructuredData = createProfilePageStructuredData({
      url: currentUrl,
      name: coach.fullName,
      description,
      image: coach.profile.mainImage || coach.profilePictureUrl || imageUrl,
      jobTitle: coach.profile.title || coach.profile.coachingTitle || undefined,
      sameAs: socialLinks,
    });

    const breadcrumbStructuredData = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
      { name: coach.fullName, url: currentUrl },
    ]);
    const serviceStructuredData = createServiceStructuredData({
      name: `${coach.profile.coachingTitle || "Coaching"} with ${coach.fullName}`,
      description,
      provider: coach.fullName,
      url: currentUrl,
      image: coach.profile.mainImage || coach.profilePictureUrl || imageUrl,
    });

    // Add profile-specific OpenGraph tags
    const profileMetaTags = [
      // Profile-specific metadata
      ...(coach.firstName
        ? [
            {
              property: "og:profile:first_name" as const,
              content: coach.firstName,
            },
          ]
        : []),
      ...(coach.lastName
        ? [
            {
              property: "og:profile:last_name" as const,
              content: coach.lastName,
            },
          ]
        : []),
      {
        property: "og:profile:username" as const,
        content: coach.username,
      },
      // Geographic and language metadata
      ...(coach.profile?.primaryCity
        ? [
            {
              name: "geo.placename" as const,
              content: coach.profile.primaryCity,
            },
          ]
        : []),
      ...(coach.profile?.languages
        ? [
            {
              name: "language" as const,
              content: coach.profile.languages,
            },
          ]
        : []),
    ];

    return {
      meta: [...(metaTags.meta || []), ...profileMetaTags],
      links: metaTags.links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(personStructuredData),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(profilePageStructuredData),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbStructuredData),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceStructuredData),
        },
      ],
    };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { coach, debugData, hasWheelPage } = Route.useLoaderData();

  useEffect(() => {
    if (coach?.username) {
      const source =
        new URLSearchParams(window.location.search).get("ref") ?? undefined;
      trackCoachProfileViewed(coach.username, source);
    }
  }, [coach?.username]);

  const { username } = Route.useParams();

  if (!coach?.profile) {
    return <CoachNotFound username={username} />;
  }

  const ratingAggregate = getTestimonialAggregate(coach.testimonials ?? []);

  return (
    <>
      {coach.profile.isPreGeneratedProfile && (
        <PreGeneratedProfileBanner
          coachName={coach.firstName}
          fullName={coach.fullName}
          username={coach.username}
        />
      )}

      <CoachHero coach={coach} badgeSlot={renderBadgeSlot(coach)} />

      <CoachTrustBand coach={coach} rating={ratingAggregate} />

      <CoachProfileSections
        coach={coach}
        hasWheelPage={hasWheelPage}
        tabNav={({ sectionNumbers, credentialCount }) => (
          <CoachTabNav
            sectionNumbers={sectionNumbers}
            testimonialCount={coach.testimonials?.length ?? 0}
            credentialCount={credentialCount}
          />
        )}
      />

      {debugData && <CoachMatchingDebug data={debugData} coachId={coach.id} />}
    </>
  );
}

/**
 * Renders the optional "Founding Coach" line above the hero kicker.
 * Returns null for everyone else — the slot is reserved for the editorial
 * earned distinction.
 */
function renderBadgeSlot(coach: { isFoundingCoach?: boolean | null }) {
  if (!coach.isFoundingCoach) return null;

  return (
    <div className="mb-3 inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase">
      <span aria-hidden className="size-1.5 rounded-full bg-amber-500" />
      Founding Coach
    </div>
  );
}
